import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase, insertWithUserId, handleSupabaseError } from "@/lib/hooks/api/utils";
import {
  Task,
  CreateTaskForm,
  UpdateTaskForm,
  CreateTaskFormSchema,
  UpdateTaskFormSchema,
} from "@/lib/schemas/task";
import { applyTaskPrimaryEntityFields } from "@/lib/domain/task-entity-linkage";
import { useBustDashboardCache } from "@/lib/hooks/api/dashboard/use-dashboard-summary";

export function useCreateTask() {
  const queryClient = useQueryClient();
  const bustDashboard = useBustDashboardCache();

  return useMutation({
    mutationFn: async (taskData: CreateTaskForm) => {
      const validatedData = CreateTaskFormSchema.parse(taskData);
      const payload = applyTaskPrimaryEntityFields(validatedData);
      const { data, error } = await insertWithUserId("contact_tasks", payload);

      if (error) {
        handleSupabaseError(error, "create task");
      }

      return data;
    },
    retry: 0,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["tasks-paginated"] });
      void bustDashboard(); // new task changes the dashboard task stats

      if (data?.tags) {
        void queryClient.invalidateQueries({ queryKey: ["tags", "tasks"] });
        fetch("/api/tags/invalidate/tasks", { method: "POST" }).catch((error) => {
          console.error("Failed to invalidate server-side task tags cache:", error);
        });
      }
    },
    onError: (error: Error) => {
      console.error("Error creating task:", error);
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  const bustDashboard = useBustDashboardCache();

  return useMutation({
    mutationFn: async (taskData: UpdateTaskForm) => {
      const validatedData = UpdateTaskFormSchema.parse(taskData);
      const supabase = getSupabase();
      const { id, ...updateData } = validatedData;

      if (updateData.status === "completed" && !updateData.completed_at) {
        updateData.completed_at = new Date().toISOString();
      }
      if (updateData.status !== "completed") {
        updateData.completed_at = null;
      }

      const { data, error } = await supabase
        .from("contact_tasks")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Error updating task:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onMutate: async (taskData: UpdateTaskForm) => {
      await queryClient.cancelQueries({ queryKey: ["tasks"] });
      await queryClient.cancelQueries({ queryKey: ["tasks-paginated"] });
      await queryClient.cancelQueries({ queryKey: ["task", taskData.id] });

      const previousTasks = queryClient.getQueryData<Task[]>(["tasks"]);
      const previousTasksPaginated = queryClient.getQueryData<{ data: Task[] }>(["tasks-paginated"]);
      const previousTask = queryClient.getQueryData<Task>(["task", taskData.id]);

      queryClient.setQueryData(["task", taskData.id], (old: Task | undefined) => {
        if (!old) return old;
        return { ...old, ...taskData };
      });

      queryClient.setQueryData(["tasks"], (old: Task[] | undefined) => {
        if (!old) return old;
        return old.map((task) => (task.id === taskData.id ? { ...task, ...taskData } : task));
      });

      queryClient.setQueryData(["tasks-paginated"], (old: { data: Task[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data.map((task: Task) =>
            task.id === taskData.id ? { ...task, ...taskData } : task
          ),
        };
      });

      return { previousTasks, previousTasksPaginated, previousTask };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["task", data.id], data);

      queryClient.setQueryData(["tasks"], (old: Task[] | undefined) => {
        if (!old) return old;
        return old.map((task) => (task.id === data.id ? data : task));
      });

      queryClient.setQueryData(["tasks-paginated"], (old: { data: Task[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data.map((task: Task) => (task.id === data.id ? data : task)),
        };
      });

      if (data?.tags) {
        void queryClient.invalidateQueries({ queryKey: ["tags", "tasks"] });
        fetch("/api/tags/invalidate/tasks", { method: "POST" }).catch((error) => {
          console.error("Failed to invalidate server-side task tags cache:", error);
        });
      }

      void bustDashboard(); // status change (e.g. completed) changes the dashboard task stats
      toast.success("Task updated successfully");
    },
    onError: (error: Error, _variables, context: { previousTasks?: Task[]; previousTasksPaginated?: { data: Task[] }; previousTask?: Task } | undefined) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(["tasks"], context.previousTasks);
      }
      if (context?.previousTasksPaginated) {
        queryClient.setQueryData(["tasks-paginated"], context.previousTasksPaginated);
      }
      if (context?.previousTask) {
        queryClient.setQueryData(["task", _variables.id], context.previousTask);
      }

      toast.error("Failed to update task: " + error.message);
      console.error("Error updating task:", error);
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  const bustDashboard = useBustDashboardCache();

  return useMutation({
    mutationFn: async (id: string) => {
      const supabase = getSupabase();
      const { error } = await supabase.from("contact_tasks").delete().eq("id", id);

      if (error) {
        console.error("Error deleting task:", error);
        throw new Error(error.message);
      }

      return id;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["tasks-paginated"] });
      void bustDashboard(); // removing a task changes the dashboard task stats

      void queryClient.invalidateQueries({ queryKey: ["tags", "tasks"] });
      fetch("/api/tags/invalidate/tasks", { method: "POST" }).catch((error) => {
        console.error("Failed to invalidate server-side task tags cache:", error);
      });

      toast.success("Task deleted successfully");
    },
    onError: (error: Error) => {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task: " + error.message);
    },
  });
}
